/* The Evidence tab's row model (Ale ruling 2026-09-05, flat v2): ONE LINE
 * PER OBJECT + OBLIGATION, a pure flat table.
 *
 * The matrix endpoint is flat (object × obligation × status); this module
 * joins it to the record-table payload so every line carries its object's
 * identity INLINE — the kind dot, the name in the object's colour, then
 * "kind · counterpart" — and orders the lines plainly: entities, then
 * instruments, then positions, then trades; within a kind by name A–Z;
 * within an object in the policy's demand order. No hierarchy, no holder
 * lines, no indentation, no "counted once", no focused view: the picker in
 * the title narrows, nothing else does.
 *
 * Pure (imports only the equally pure chainTree / recordDerive) so the bare
 * node runner can prove the layout — see tests/unit/evidenceRows.test.mjs.
 * Dates stay ISO here; the component formats them. */

import { chainOf, fundRootId, type ChainSegment } from './chainTree.js'
import { figure, deltaWords } from './recordDerive.js'

/* ── inputs ─────────────────────────────────────────────────────────── */

export type EvStatus = 'open' | 'requested' | 'pending_signoff' | 'satisfied' | 'waived'

/** One matrix row. The document / page / dated / request / waiver fields
 *  are OPTIONAL: an endpoint that does not carry them yet leaves them
 *  absent and the table prints a dash. */
export interface EvMatrixRow {
  object_type: 'entity' | 'instrument' | 'position' | 'trade'
  object_id: string
  obligation_code: string
  status: EvStatus
  is_mandatory: boolean
  /** The demand sits on a plug trade — an officer reconciling movement or
   *  an inception balance — so an owed line says "on a plug" (batch B). */
  on_plug?: boolean | null
  document_id?: string | null
  document_name?: string | null
  document_kind?: string | null
  page?: number | null
  page_to?: number | null
  dated?: string | null
  valid_to?: string | null
  requested_at?: string | null
  owed_since?: string | null
  waived_by_name?: string | null
  waived_by_role?: string | null
  waived_reason?: string | null
  waived_at?: string | null
}

export interface EvSegment extends ChainSegment {
  units_held: number | null
  units_currency: string | null
  committed_amount: number | null
}

export interface EvEntity {
  id: string
  name: string
  legal_form: string | null
  jurisdiction: string | null
  entity_nature?: string | null
}

export interface EvInstrument {
  id: string
  name: string
  issuer_name: string | null
  outstanding_units?: number | null
}

export interface EvPosition {
  id: string
  holder_name: string | null
  instrument_name: string | null
  issuer_name?: string | null
  units_held: number | null
  units_currency: string | null
  committed_amount: number | null
}

export interface EvTrade {
  id: string
  position_id: string | null
  instrument_id: string | null
  transaction_type: string
  units_delta: number | null
  amount: number | null
  currency: string | null
  effective_date: string | null
  document: string | null
}

export interface EvRecord {
  fund_name: string
  segments: EvSegment[]
  entities: EvEntity[]
  instruments: EvInstrument[]
  positions: EvPosition[]
  trades: EvTrade[]
}

/* ── the words ───────────────────────────────────────────────────────── */

/** Canonical code order — the demands of one object print in this order. */
export const DEMAND_ORDER = ['E1', 'E2', 'B1', 'B2', 'B3', 'M1', 'M2', 'M3', 'V1'] as const

/** What the demand asks for, in plain words ("B1 · holder confirmation"). */
export const DEMAND_WORDS: Record<string, string> = {
  E1: 'constitutional document',
  E2: 'register extract',
  B1: 'holder confirmation',
  B2: 'commitment confirmation',
  B3: 'issuer register',
  M1: 'movement document',
  M2: 'cash movement',
  M3: 'authorization',
  V1: 'valuation',
}

/** The kind of document an owed demand expects — printed faint in the
 *  DOCUMENT column until one lands. */
export const EXPECTED_KIND: Record<string, string> = {
  E1: 'articles of association',
  E2: 'register extract',
  B1: 'custodian or issuer statement',
  B2: 'issuer statement',
  B3: 'share register',
  M1: 'contract or notice',
  M2: 'bank advice',
  M3: 'IC memo or resolution',
  V1: 'valuation report',
}

export type ObjKind = 'entity' | 'instrument' | 'position' | 'trade'

export const KIND_WORD: Record<ObjKind, string> = {
  entity: 'entity',
  instrument: 'instrument',
  position: 'position',
  trade: 'trade',
}

export function demandWords(code: string): string {
  return DEMAND_WORDS[code] ?? code
}

/** A trade kind the officer reads by another name (plug doctrine, Ale
 *  2026-09-04): the fictitious first trade of a migrated holding is its
 *  "inception balance" — a starting line, never "pre-existing ownership".
 *  Read-path wording only; the record keeps its vocabulary. */
export const TRADE_WORDS: Record<string, string> = {
  pre_existing_ownership: 'inception balance',
}

export function tradeWords(transactionType: string | null | undefined): string {
  return TRADE_WORDS[transactionType ?? ''] ?? (words(transactionType) || 'trade')
}

/** The signer's role in officer words ("ops officer", "manager"). */
export function roleWords(role: string | null | undefined): string | null {
  return role ? words(role) || null : null
}

export function isProven(status: EvStatus): boolean {
  return status === 'satisfied' || status === 'waived'
}

/* ── the model ───────────────────────────────────────────────────────── */

/** The object's colour: entity navy · instrument blue · position gold. A
 *  trade wears the colour of what it moves. */
export type ObjColour = 'ent' | 'ins' | 'pos'

export interface EvObject {
  kind: ObjKind
  id: string
  /** The identifier — never a sentence. */
  name: string
  /** The identity inline: "kind · counterpart". */
  sub: string
  colour: ObjColour
  /** The drawer target (a package opens on its commitment record). */
  target: { type: ObjKind; id: string }
  /** The record ids the object stands for (a commitment package = two). */
  ids: string[]
}

export interface EvDemand {
  code: string
  words: string
  status: EvStatus
  proven: boolean
  mandatory: boolean
  /** An unmet demand policy only RECOMMENDS: the STATUS cell reads
   *  "O optional", muted — never "✗ owed"; the Owed chip leaves it out. */
  optional: boolean
  document: { id: string | null; name: string; kind: string | null } | null
  /** The kind an owed row expects. */
  expectedKind: string
  /** "p.2" / "p.1–4". */
  page: string | null
  dated: string | null
  validTo: string | null
  requestedAt: string | null
  owedSince: string | null
  waivedBy: string | null
  /** The signer's role in officer words. */
  waivedRole: string | null
  waivedAt: string | null
  waivedReason: string | null
  /** The demand sits on a plug trade: an owed line says "on a plug". */
  onPlug: boolean
  /** The object the demand sits on — repeated on every line. */
  object: EvObject
}

export interface EvCounts {
  total: number
  proven: number
  /** Every unmet demand — optional ones included. */
  owed: number
  /** Of `owed`, the ones policy only recommends. */
  optional: number
  waived: number
}

/* ── naming ──────────────────────────────────────────────────────────── */

function words(s: string | null | undefined): string {
  return (s ?? '').replace(/_/g, ' ').trim()
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "2026-06-30" → "30 Jun 2026" (the officer date canon; anything else
 *  passes through). Kept here so the module stays import-free of lib/. */
export function officerDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return iso
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1] ?? m[2]} ${m[1]}`
}

/** "2026-06-30" → "30 Jun 2026", "2026-01-09" → "09 Jan 2026": the table
 *  form, a two-digit day so month and year line up. */
export function tableDate(iso: string | null | undefined): string | null {
  const d = officerDate(iso)
  return d ? d.replace(/^(\d) /, '0$1 ') : null
}

function money(amount: number | null, ccy: string | null): string | null {
  if (amount === null || amount === undefined) return null
  return ccy ? `${ccy} ${figure(amount)}` : figure(amount)
}

function join(parts: (string | null | undefined)[]): string {
  return parts.filter((p): p is string => !!p && p.trim() !== '').join(' · ')
}

class Names {
  private ent: Map<string, EvEntity>
  private ins: Map<string, EvInstrument>
  private pos: Map<string, EvPosition>
  private legById: Map<string, EvSegment>
  private legByInstrument: Map<string, EvSegment>
  root: string | null

  constructor(
    private record: EvRecord,
    chain: EvSegment[]
  ) {
    this.ent = new Map(record.entities.map((e) => [e.id, e]))
    this.ins = new Map(record.instruments.map((i) => [i.id, i]))
    this.pos = new Map((record.positions ?? []).map((p) => [p.id, p]))
    this.legById = new Map()
    this.legByInstrument = new Map()
    for (const s of chain) {
      for (const id of s.position_ids.length ? s.position_ids : [s.position_id])
        this.legById.set(id, s)
      this.legById.set(s.position_id, s)
      if (s.instrument_id) this.legByInstrument.set(s.instrument_id, s)
    }
    this.root = fundRootId(chain)
  }

  entity(id: string): EvObject {
    const e = this.ent.get(id)
    const isRoot = id === this.root
    const fundInRecord = !isRoot && e?.entity_nature === 'collective_vehicle'
    return {
      kind: 'entity',
      id,
      name: e?.name ?? 'entity',
      sub: join([
        'entity',
        isRoot ? 'the fund' : fundInRecord ? 'a fund in the record' : null,
        e?.legal_form,
        e?.jurisdiction,
      ]),
      colour: 'ent',
      target: { type: 'entity', id },
      ids: [id],
    }
  }

  instrument(id: string): EvObject {
    const i = this.ins.get(id)
    const leg = this.legByInstrument.get(id)
    const issued =
      i?.outstanding_units !== null && i?.outstanding_units !== undefined
        ? `${figure(i.outstanding_units)} issued`
        : null
    return {
      kind: 'instrument',
      id,
      name: i?.name ?? leg?.instrument_name ?? 'instrument',
      sub: join(['instrument', i?.issuer_name ?? leg?.issuer_name, issued]),
      colour: 'ins',
      target: { type: 'instrument', id },
      ids: [id],
    }
  }

  /** A position — a commitment package reads as ONE object under its
   *  commitment record, both records' demands on it. */
  position(id: string): EvObject {
    const leg = this.legById.get(id)
    if (leg) {
      const ids = leg.position_ids.length ? leg.position_ids : [leg.position_id]
      return {
        kind: 'position',
        id: leg.position_id,
        name: leg.instrument_name ?? 'instrument',
        sub: `position · ${leg.holder_name ?? 'a holder'} › ${leg.issuer_name ?? 'an issuer'}`,
        colour: 'pos',
        target: { type: 'position', id: leg.position_id },
        ids,
      }
    }
    const p = this.pos.get(id)
    return {
      kind: 'position',
      id,
      name: p?.instrument_name ?? 'instrument',
      sub: p
        ? `position · ${p.holder_name ?? 'a holder'}${p.issuer_name ? ` › ${p.issuer_name}` : ''}`
        : 'position',
      colour: 'pos',
      target: { type: 'position', id },
      ids: [id],
    }
  }

  trade(t: EvTrade): EvObject {
    const instrument =
      (t.instrument_id ? this.ins.get(t.instrument_id)?.name : null) ??
      (t.position_id ? this.pos.get(t.position_id)?.instrument_name : null) ??
      (t.position_id ? this.legById.get(t.position_id)?.instrument_name : null) ??
      (t.instrument_id ? this.legByInstrument.get(t.instrument_id)?.instrument_name : null) ??
      'the instrument'
    const amount = money(t.amount, t.currency)
    const units =
      t.units_delta !== null && t.units_delta !== undefined
        ? t.position_id
          ? deltaWords(t.units_delta)
          : `${figure(t.units_delta)} issued`
        : null
    const what = units && amount ? `${units} for ${amount}` : (units ?? amount)
    return {
      kind: 'trade',
      id: t.id,
      name: join([tradeWords(t.transaction_type), tableDate(t.effective_date)]),
      sub: join([t.position_id ? 'trade' : 'instrument trade', instrument, what]),
      // a position trade moves a position (gold); an instrument trade moves
      // the instrument itself (blue)
      colour: t.position_id ? 'pos' : 'ins',
      target: { type: 'trade', id: t.id },
      ids: [t.id],
    }
  }

  any(id: string, type: ObjKind, trades: Map<string, EvTrade>): EvObject {
    switch (type) {
      case 'entity':
        return this.entity(id)
      case 'instrument':
        return this.instrument(id)
      case 'position':
        return this.position(id)
      case 'trade': {
        const t = trades.get(id)
        return t
          ? this.trade(t)
          : {
              kind: 'trade',
              id,
              name: 'trade',
              sub: 'trade',
              colour: 'pos',
              target: { type: 'trade', id },
              ids: [id],
            }
      }
    }
  }
}

/* ── demands ─────────────────────────────────────────────────────────── */

function pageWords(from: number | null | undefined, to: number | null | undefined): string | null {
  if (from === null || from === undefined) return null
  return to && to !== from ? `p.${from}–${to}` : `p.${from}`
}

/** A trade's own document label on the record ("N04.pdf", or the fixture's
 *  "N04 p.3") — the M1 fallback while the matrix carries no document. */
export function tradeDocument(label: string | null): { name: string; page: string | null } | null {
  if (!label) return null
  const m = /^(.*?)\s+p\.(\d+)$/.exec(label.trim())
  if (m) return { name: m[1], page: `p.${m[2]}` }
  return { name: label.trim(), page: null }
}

export function demandOf(r: EvMatrixRow, object: EvObject, trade: EvTrade | null): EvDemand {
  const proven = isProven(r.status)
  let document: EvDemand['document'] = null
  let page = pageWords(r.page, r.page_to)
  if (r.document_id || r.document_name) {
    document = {
      id: r.document_id ?? null,
      name: r.document_name ?? 'document',
      kind: r.document_kind ?? null,
    }
  } else if (proven && r.status === 'satisfied' && trade && r.obligation_code === 'M1') {
    const d = tradeDocument(trade.document)
    if (d) {
      document = { id: null, name: d.name, kind: null }
      page = page ?? d.page
    }
  }
  return {
    code: r.obligation_code,
    words: demandWords(r.obligation_code),
    status: r.status,
    proven,
    mandatory: r.is_mandatory,
    optional: !proven && !r.is_mandatory,
    document,
    expectedKind: EXPECTED_KIND[r.obligation_code] ?? 'document',
    page,
    // DATED is the document's date only: a waived line has none — the
    // waiver's date lives in the STATUS mark (batch B §12, Ale 2026-09-04)
    dated: r.status === 'waived' ? null : (r.dated ?? null),
    validTo: r.valid_to ?? null,
    requestedAt: r.requested_at ?? null,
    owedSince: r.owed_since ?? null,
    waivedBy: r.waived_by_name ?? null,
    waivedRole: roleWords(r.waived_by_role),
    // an older payload dated the waived row with the waiver's day
    waivedAt: r.waived_at ?? (r.status === 'waived' ? (r.dated ?? null) : null),
    waivedReason: r.waived_reason?.trim() || null,
    onPlug: !!r.on_plug,
    object,
  }
}

function orderOf(code: string): number {
  const i = (DEMAND_ORDER as readonly string[]).indexOf(code)
  return i === -1 ? DEMAND_ORDER.length : i
}

const KIND_RANK: Record<ObjKind, number> = { entity: 0, instrument: 1, position: 2, trade: 3 }

const byName = (a: string, b: string) => a.localeCompare(b, 'en', { sensitivity: 'base' })

/** The plain order: entities, instruments, positions, trades; by name
 *  A–Z (then the identity line, so two instruments of one name sit by
 *  issuer); one object's demands in the policy's order. */
export function lineOrder(a: EvDemand, b: EvDemand): number {
  return (
    KIND_RANK[a.object.kind] - KIND_RANK[b.object.kind] ||
    byName(a.object.name, b.object.name) ||
    byName(a.object.sub, b.object.sub) ||
    orderOf(a.code) - orderOf(b.code)
  )
}

/* ── the build ───────────────────────────────────────────────────────── */

/** One line per matrix row, its object's identity inline, in the plain
 *  order. The two records of a commitment package fold into ONE object. */
export function flatEvidence(record: EvRecord, matrix: EvMatrixRow[]): EvDemand[] {
  const chain = chainOf(record.segments)
  const names = new Names(record, chain)
  const trades = new Map(record.trades.map((t) => [t.id, t]))
  const lines = matrix.map((r) => {
    const object = names.any(r.object_id, r.object_type, trades)
    return demandOf(r, object, r.object_type === 'trade' ? (trades.get(r.object_id) ?? null) : null)
  })
  return lines.sort(lineOrder)
}

/** The counts the tab sentence and the chips print; waived = proven. */
export function evidenceCounts(matrix: { status: EvStatus; is_mandatory: boolean }[]): EvCounts {
  const c: EvCounts = { total: matrix.length, proven: 0, owed: 0, optional: 0, waived: 0 }
  for (const r of matrix) {
    if (isProven(r.status)) {
      c.proven += 1
      if (r.status === 'waived') c.waived += 1
    } else {
      c.owed += 1
      if (!r.is_mandatory) c.optional += 1
    }
  }
  return c
}

/* ── the toolbar: the chips and the find box ─────────────────────────── */

export type EvShow = 'all' | 'proven' | 'owed' | 'optional' | 'waived'

export function keepLine(d: EvDemand, show: EvShow): boolean {
  switch (show) {
    case 'proven':
      return d.proven
    case 'owed':
      // owed = unmet AND mandatory: an optional demand is neither proven
      // nor owed, so it prints only under All (and Optional)
      return !d.proven && d.mandatory
    case 'optional':
      return d.optional
    case 'waived':
      return d.status === 'waived'
    default:
      return true
  }
}

export function matchesLine(d: EvDemand, q: string): boolean {
  return [d.object.name, d.object.sub, d.code, d.words, d.document?.name, d.document?.kind]
    .filter((v): v is string => !!v)
    .some((v) => v.toLowerCase().includes(q))
}

/** Owed = unmet AND mandatory: an optional demand is neither proven nor
 *  owed, so it prints only under All and Optional — UNLESS the officer
 *  asked to include optional (Ale 2026-09-05), when the Owed view lists
 *  the recommended ones too so he can request them before signing. No
 *  count moves either way; only what is listed does. */
export function filterEvidence(
  lines: EvDemand[],
  show: EvShow,
  query: string,
  includeOptional = false
): EvDemand[] {
  const q = query.trim().toLowerCase()
  const keep = (d: EvDemand) => (show === 'owed' && includeOptional ? !d.proven : keepLine(d, show))
  return lines.filter((d) => keep(d) && (!q || matchesLine(d, q)))
}

/** The optional demands still owed — unmet, and policy only RECOMMENDS
 *  them. The "include optional" listing (the chain-status block, the
 *  Evidence tab, the sign-off card) reads these; one line per demand. */
export function optionalDemands(lines: EvDemand[]): EvDemand[] {
  const out: EvDemand[] = []
  const seen = new Set<string>()
  for (const d of lines) {
    if (!d.optional) continue
    const key = `${d.object.kind}:${d.object.id}:${d.code}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(d)
  }
  return out
}
